/**
 * DOCUMENTATION:
 * Component: AIMentorSidebar
 * Purpose: Professional RAG-powered Chat Interface.
 * * * FOR JUNIOR DEVELOPERS:
 * 1. Functional Updates: We use setMessages(prev => ...) because streaming
 * happens fast. This ensures we don't accidentally "overwrite" messages if
 * multiple chunks arrive at the same time.
 * 2. Auto-Scroll: The 'behavior: smooth' makes the chat feel premium.
 * 3. Conditional UI: We show "Searching..." only until the first word of
 * the AI response appears.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
    Drawer, Box, Typography, IconButton, TextField, Stack,
    Avatar, Paper, CircularProgress, Tooltip, Divider, styled
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

// Internal Hooks & Services
import { useAI } from '../../context/AIProvider';
import { searchLessons } from '../../services/localRAG';
import { getGeminiStreamResponse } from '../../services/aiService';

// --- Styled Components ---

const ChatContainer = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    overflowY: 'auto',
    marginBottom: theme.spacing(2),
    paddingRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    scrollBehavior: 'smooth', // Added for a premium feel
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(167, 139, 250, 0.3)', borderRadius: '10px' },
}));

const MessageBubble = styled(Paper)(({ theme, role }) => ({
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    maxWidth: '85%',
    alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
    backgroundColor: role === 'user' ? '#E5C95F' : 'rgba(255, 255, 255, 0.05)',
    color: role === 'user' ? '#100720' : 'white', // Gold/Dark theme for user bubbles
    fontWeight: role === 'user' ? 600 : 400,
    borderRadius: role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    border: role === 'user' ? 'none' : '1px solid rgba(167, 139, 250, 0.2)',
}));

export default function AIMentorSidebar() {
    const {
        isSidebarOpen, setSidebarOpen, messages, setMessages,
        isThinking, setIsThinking, clearChat
    } = useAI();

    const [input, setInput] = useState("");
    const scrollRef = useRef(null);

    // Auto-scroll logic
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isThinking]);

    const handleSend = async () => {
        if (!input.trim() || isThinking) return;

        const userQuery = input;
        setInput("");

        // 1. Add User Message
        setMessages(prev => [...prev, { role: 'user', content: userQuery }]);
        setIsThinking(true);

        try {
            // 2. RETRIEVAL PHASE
            const searchResults = await searchLessons(userQuery);
            const contextText = searchResults.length > 0
                ? searchResults.map(res => res.pageContent).join("\n\n---\n\n")
                : "General knowledge (No specific lesson found).";

            // 3. INITIALIZE AI PLACEHOLDER
            setMessages(prev => [...prev, { role: 'assistant', content: "" }]);

            // 4. GENERATION PHASE (Streaming)
            let accumulatedResponse = "";

            await getGeminiStreamResponse(userQuery, contextText, (chunk) => {
                accumulatedResponse += chunk;

                // Precision Update: Only targets the newest assistant message
                setMessages(prev => {
                    const newMsgs = [...prev];
                    const lastIdx = newMsgs.length - 1;
                    if (lastIdx >= 0 && newMsgs[lastIdx].role === 'assistant') {
                        newMsgs[lastIdx].content = accumulatedResponse;
                    }
                    return newMsgs;
                });
            });

        } catch (error) {
            console.error("Mentor Service Error:", error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "I hit a snag in my neural network. Please check your connection and try again!"
            }]);
        } finally {
            setIsThinking(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
            transitionDuration={400}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 420 },
                    bgcolor: '#100720', // Matched to your BRAND.dark
                    color: 'white',
                    borderLeft: '1px solid rgba(229, 201, 95, 0.2)', // Matched to BRAND.gold
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* --- Header --- */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: '#E5C95F', color: '#100720' }}>
                            <PsychologyIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={800} lineHeight={1}>ProDev Mentor</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.5 }}>RAG-Streaming Engine Active</Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row">
                        <Tooltip title="Clear chat">
                            <IconButton onClick={clearChat} sx={{ color: 'rgba(255,255,255,0.3)', mr: 0.5 }}>
                                <DeleteSweepIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={() => setSidebarOpen(false)} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.08)' }} />

                {/* --- Chat Content --- */}
                <ChatContainer ref={scrollRef}>
                    {messages.length === 0 && (
                        <Box sx={{ mt: '40%', textAlign: 'center', opacity: 0.4, px: 4 }}>
                            <Typography variant="body1" fontWeight={700} gutterBottom>👋 Hello, Architect!</Typography>
                            <Typography variant="body2">Ask me anything about GCP, MongoDB, or System Design.</Typography>
                        </Box>
                    )}

                    {messages.map((m, i) => (
                        <MessageBubble key={i} role={m.role} elevation={0}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                {m.content || (m.role === 'assistant' && "...")}
                            </Typography>
                        </MessageBubble>
                    ))}

                    {/* Show searching status while context is being fetched */}
                    {isThinking && messages[messages.length - 1]?.content === "" && (
                        <Stack direction="row" spacing={1} sx={{ opacity: 0.6, ml: 1, mb: 2 }}>
                            <CircularProgress size={12} sx={{ color: '#E5C95F' }} />
                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>Analyzing curriculum...</Typography>
                        </Stack>
                    )}
                </ChatContainer>

                {/* --- Input --- */}
                <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <Stack direction="row" spacing={1} alignItems="flex-end">
                        <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            size="small"
                            placeholder="Type a message..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.03)',
                                borderRadius: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': { borderColor: 'rgba(229, 201, 95, 0.2)' },
                                    '&:hover fieldset': { borderColor: '#E5C95F' },
                                    '&.Mui-focused fieldset': { borderColor: '#E5C95F' },
                                }
                            }}
                        />
                        <IconButton
                            onClick={handleSend}
                            disabled={isThinking || !input.trim()}
                            sx={{
                                bgcolor: '#E5C95F',
                                color: '#100720',
                                mb: 0.5,
                                '&:hover': { bgcolor: '#F0D670' },
                                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.2)' }
                            }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>
            </Box>
        </Drawer>
    );
}
